import React, { useState, useEffect, useRef } from "react";
import { API_URL } from "../../environment";
import Swal from "sweetalert2";

// Tipagem para o SDK do Facebook
declare global {
  interface Window {
    FB: any;
    fbAsyncInit: any;
  }
}

const Connections = () => {
  const [loading, setLoading] = useState(false);
  const [id_time] = useState(localStorage.getItem("id_time"));
  const [instanceName] = useState(localStorage.getItem("instanceName"));

  // Estados Conexão Web
  const [status, setStatus] = useState<string>("Buscando...");
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [modalMessage, setModalMessage] = useState("");
  const [showRetryConnection, setShowRetryConnection] = useState(false);
  const isConnecting = useRef(false);

  // Estados API Oficial
  const [officialConfig, setOfficialConfig] = useState<any>(null);
  const [fbAppId, setFbAppId] = useState("");
  const [configId, setConfigId] = useState("");

  

  useEffect(() => {
    checkStatus();
    loadOfficialConfig();
    const interval = setInterval(checkStatus, 15000);
    return () => clearInterval(interval);
  }, [instanceName, id_time]);

  const loadOfficialConfig = async () => {
    if (!id_time) return;
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/get_official_config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_time }),
      });
      if (res.ok) {
        const data = await res.json();
        setOfficialConfig(data);
      } else {
        setOfficialConfig(null);
      }
    } catch (err) {
      console.error("Erro ao carregar config oficial:", err);
    }
  };

  const triggerReconnection = async (instName: string) => {
    setLoading(true); // Ativa trava visual
    setQrCode(null);
    setModalMessage("Iniciando conexão...");
    
    try {
      const res = await fetch(`${API_URL}/reconectarinstancia`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instanceName: instName }),
      });
      
      const data = await res.json();
      console.log("DEBUG - Resposta uazapi Completa:", data);

      // Tenta capturar o QR de várias formas possíveis (compatibilidade)
      const rawQr = data.base64 || data.qrcode?.base64 || data.code || (data.results && data.results[0]?.qrcode);
      
      if (rawQr) {
        console.log("✅ QR Code capturado com sucesso!");
        const formattedQr = rawQr.startsWith('data:') ? rawQr : `data:image/png;base64,${rawQr}`;
        setQrCode(formattedQr);
        setModalMessage("Escaneie para conectar");
      } else {
        console.warn("⚠️ API não retornou QR Code no campo esperado.");
        setModalMessage("Aguardando QR Code...");
      }

      // Inicia o polling de status
      monitorConnection(instName);
    } catch (err) {
      console.error("❌ Erro fatal na reconexão:", err);
      setModalMessage("Falha ao gerar conexão.");
      setShowRetryConnection(true);
    } finally {
      setLoading(false); // Libera trava visual
    }
  };

  const monitorConnection = async (instName: string) => {
    let attempts = 0;
    const maxAttempts = 30;
    while (attempts < maxAttempts) {
      try {
        const res = await fetch(`${API_URL}/pegarinstancias`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ instanceName: instName }),
        });
        
        if (res.ok) {
          const data = await res.json();
          const inst = data[0];

          // REDUNDÂNCIA: Se o QR Code apareceu no polling e não temos ele na tela, captura!
          if (inst?.qrCode && !qrCode) {
            console.log("📡 QR Code capturado via Polling!");
            const rawQr = inst.qrCode;
            const formattedQr = typeof rawQr === 'string' && rawQr.startsWith('data:') 
              ? rawQr 
              : `data:image/png;base64,${typeof rawQr === 'object' ? rawQr.base64 : rawQr}`;
            
            setQrCode(formattedQr);
            setModalMessage("Escaneie para conectar");
          }

          if (inst?.connectionStatus === "open" || inst?.connectionStatus === "connected") {
            setQrCode(null);
            setModalMessage("✅ Conectado!");
            setStatus("open");
            Swal.fire({ title: "Sucesso!", text: "WhatsApp Conectado", icon: "success", timer: 2000 });
            return;
          }
        }
      } catch (e) { console.error("Erro no monitoramento:", e); }
      
      attempts++;
      await new Promise(r => setTimeout(r, 3000));
    }
    setModalMessage("O tempo de conexão expirou.");
    setShowRetryConnection(true);
  };

  const handleReconnect = () => {
    if (!instanceName) return;
    setQrCode(null);
    setShowRetryConnection(false);
    setModalMessage("Iniciando conexão..."); // Usaremos isso como texto de status
    triggerReconnection(instanceName);
  };

  const checkStatus = async () => {
    if (!instanceName) {
      setStatus("Instância não configurada");
      return;
    }
    try {
      const res = await fetch(`${API_URL}/pegarinstancias`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instanceName }),
      });
      if (res.ok) {
        const data = await res.json();
        const currentStatus = data[0]?.connectionStatus || "unknown";
        setStatus(currentStatus);
      }
    } catch (err) {
      console.error("Erro ao verificar status:", err);
    }
  };

  const initMetaSdk = (appId: string) => {
    const oldScript = document.getElementById('facebook-jssdk');
    if (oldScript) oldScript.remove();

    window.fbAsyncInit = function() {
      window.FB.init({
        appId: appId,
        cookie: true,
        xfbml: true,
        version: 'v21.0'
      });
    };

    (function(d, s, id) {
      var js, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      js = d.createElement(s) as HTMLScriptElement; js.id = id;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      fjs.parentNode?.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'));
  };

  const handleMetaLogin = () => {
    if (!fbAppId || !configId) {
      Swal.fire("Atenção", "Preencha o App ID e o Config ID da sua conta Meta.", "warning");
      return;
    }
    initMetaSdk(fbAppId);
    setTimeout(() => {
      window.FB.login((response: any) => {
        if (!response.authResponse) {
          Swal.fire("Erro", "Login na Meta não autorizado.", "error");
        }
      }, {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
      });
    }, 500);
  };

  useEffect(() => {
    const handler = async (event: MessageEvent) => {
      if (!event.origin.includes("facebook.com")) return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'WA_EMBEDDED_SIGNUP_SUCCESS') {
          const { waba_id, phone_number_id } = data.data;
          const token = window.FB.getAuthResponse()?.accessToken;
          await saveMetaConfig(waba_id, phone_number_id, token);
        }
      } catch (e) {}
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const saveMetaConfig = async (waba_id: string, phone_id: string, token: string) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/save_official`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_time, waba_id, phone_id, token }),
      });
      if (res.ok) {
        const result = await res.json();
        Swal.fire("Sucesso!", `Configuração salva para o número ${result.numero || ''}`, "success");
        loadOfficialConfig();
      }
    } catch (err) {
      Swal.fire("Erro", "Falha ao salvar no servidor.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterNumber = async () => {
    const { value: pin } = await Swal.fire({
      title: 'PIN de Segurança',
      text: 'Digite o PIN de 6 dígitos configurado na Meta:',
      input: 'text',
      inputValue: '123456',
      showCancelButton: true
    });
    if (!pin) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/register_number`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone_id: officialConfig.phone_id,
          token: officialConfig.official_token,
          pin: pin
        }),
      });
      if (res.ok) {
        Swal.fire("Registrado!", "Número ativo na Cloud API.", "success");
        loadOfficialConfig();
      } else {
        const errData = await res.json();
        Swal.fire("Erro", errData.details?.error?.message || "Erro no registro", "error");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const showTutorial = () => {
    Swal.fire({
      title: '<strong>Como conseguir os IDs da Meta?</strong>',
      icon: 'info',
      html: `
        <div style="text-align: left; font-size: 14px; line-height: 1.6;">
          <p>1. Vá ao <a href="https://developers.facebook.com/" target="_blank">Meta for Developers</a> e crie uma conta.</p>
          <p>2. Crie um <b>Aplicativo do tipo "Negócios"</b>.</p>
          <p>3. Adicione o produto <b>WhatsApp</b> ao seu app.</p>
          <p>4. O <b>App ID</b> aparece no topo da página do painel.</p>
          <p>5. No menu lateral, vá em <b>WhatsApp > Configuração</b> para criar seu <b>Configuration ID</b> (Embedded Signup).</p>
        </div>
      `,
      confirmButtonText: 'Entendi!'
    });
  };

  return (
    <div className="page-wrapper" style={{ background: "#f8fafc", minHeight: "100vh" }}>
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <h2 className="mb-1 fw-bold">Conexões de WhatsApp</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">Aplicação</li>
                <li className="breadcrumb-item active">Conexões</li>
              </ol>
            </nav>
          </div>
          <button className="btn btn-outline-info rounded-pill" onClick={showTutorial}>
            <i className="ti ti-help-circle me-1"></i> Tutorial de Configuração
          </button>
        </div>

        <div className="row">
          {/* LADO ESQUERDO: WEB / QR CODE */}
          <div className="col-12 col-lg-5 mb-4">
            <div className="card shadow-sm border-0 h-100" style={{ borderRadius: "24px" }}>
              <div className="card-body p-4 text-center">
                <h4 className="fw-bold mb-3">WhatsApp Web</h4>
                
                <div className="mb-4">
                  {status === "open" || status === "connected" ? (
                    <span className="badge bg-success-transparent text-success px-3 py-2 rounded-pill">   
                      <i className="ti ti-circle-check-filled me-1"></i> Conectado
                    </span>
                  ) : (
                    <div className="d-flex flex-column gap-2 align-items-center">
                      <span className="badge bg-danger-transparent text-danger px-3 py-2 rounded-pill">     
                        <i className="ti ti-circle-x-filled me-1"></i> Desconectado
                      </span>
                      <button className="btn btn-sm btn-primary rounded-pill px-3" onClick={handleReconnect} disabled={!!qrCode && !showRetryConnection}>
                        <i className="ti ti-plug me-1"></i> {qrCode ? "Gerando novo..." : "Conectar agora"}
                      </button>
                    </div>
                  )}
                </div>

                {/* PLACEHOLDER DINÂMICO PARA O QR CODE */}
                <div className="qr-code-container mb-4">
                  <div 
                    className="qr-code-placeholder bg-light rounded-4 d-flex align-items-center justify-content-center mx-auto shadow-inner" 
                    style={{ width: "200px", maxWidth: "90vw", height: "200px", border: "2px dashed #e2e8f0", overflow: "hidden", position: "relative" }}
                  >
                    {qrCode ? (
                      <img 
                        src={qrCode} 
                        alt="WhatsApp QR Code" 
                        className="img-fluid" 
                        style={{ width: "100%", height: "100%", objectFit: "contain", padding: "10px" }} 
                      />
                    ) : (status === "connecting" || loading || modalMessage.includes("Iniciando") || modalMessage.includes("Aguardando")) ? (
                      <div className="text-center">
                        <div className="spinner-border text-primary mb-2" role="status"></div>
                        <p className="small text-muted" style={{ fontSize: '10px' }}>{modalMessage || "Conectando..."}</p>
                      </div>
                    ) : status === "open" || status === "connected" ? (
                      <div className="text-success text-center">
                        <i className="ti ti-circle-check" style={{ fontSize: "60px" }}></i>
                        <p className="small fw-bold mt-2">Sincronizado</p>
                      </div>
                    ) : (
                      <div className="text-center text-muted">
                        <i className="ti ti-qrcode" style={{ fontSize: "60px", opacity: 0.3 }}></i>
                        <p className="small mt-2">Clique em conectar</p>
                      </div>
                    )}
                  </div>
                  
                  {/* MENSAGEM DE STATUS ABAIXO DO QR */}
                  {qrCode && !showRetryConnection && (
                    <p className="small text-primary fw-bold mt-3 animate__animated animate__pulse animate__infinite">
                      Escaneie para conectar
                    </p>
                  )}
                  
                  {showRetryConnection && (
                    <div className="mt-3">
                      <p className="small text-danger mb-2">Falha na conexão</p>
                      <button className="btn btn-sm btn-outline-danger rounded-pill" onClick={handleReconnect}>
                        🔄 Tentar novamente
                      </button>
                    </div>
                  )}
                </div>

                <button className="btn btn-outline-secondary btn-sm w-100 rounded-pill" onClick={checkStatus}>     
                  Atualizar Status
                </button>
              </div>
            </div>
          </div>

          {/* LADO DIREITO: ASSISTENTE API OFICIAL */}
          <div className="col-12 col-lg-7 mb-4">
            <div className="card shadow-sm border-0 h-100" style={{ borderRadius: "24px" }}>
              <div className="card-body p-4">
                
                {!officialConfig?.phone_id ? (
                  <div className="setup-wizard">
                    <h4 className="fw-bold mb-1">API Oficial Cloud</h4>
                    <p className="text-muted small mb-4">Configure seu próprio acesso profissional à Meta.</p>

                    <div className="row g-3 mb-4">
                      <div className="col-md-6">
                        <label className="form-label fw-bold small">Facebook App ID</label>
                        <input type="text" className="form-control" placeholder="Ex: 1548..." value={fbAppId} onChange={(e) => setFbAppId(e.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold small">Configuration ID</label>
                        <input type="text" className="form-control" placeholder="Ex: a7b2..." value={configId} onChange={(e) => setConfigId(e.target.value)} />
                      </div>
                    </div>

                    <div className="alert alert-light border-0 rounded-4 p-3 mb-4">
                      <h6 className="fw-bold mb-1 small"><i className="ti ti-external-link me-2"></i>Link Rápido</h6>
                      <p className="small mb-0">Crie seu app no <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer">Console da Meta</a>.</p>
                    </div>

                    <button className="btn btn-primary w-100 rounded-pill py-2" onClick={handleMetaLogin} disabled={loading}>
                      <i className="ti ti-brand-facebook me-2"></i> Iniciar Conexão com Meta
                    </button>
                  </div>
                ) : (
                  <div className="connected-status text-center py-3">
                    <div className="icon-box bg-success-transparent text-success mx-auto mb-3" style={{ width: "60px", height: "60px", borderRadius: "15px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <i className="ti ti-brand-whatsapp" style={{ fontSize: "30px" }}></i>
                    </div>
                    <h5 className="fw-bold mb-3">Sua API Oficial está Vinculada!</h5>
                    
                    <div className="text-start bg-light rounded-4 p-3 mb-4">
                      <p className="mb-1 small"><strong>Número:</strong> {officialConfig.numero || "Detectando..."}</p>
                      <p className="mb-0 small"><strong>Phone ID:</strong> {officialConfig.phone_id}</p>
                    </div>

                    <button className="btn btn-success w-100 rounded-pill py-2 mb-2" onClick={handleRegisterNumber} disabled={loading}>
                      Registrar Número na Cloud API
                    </button>
                    <p className="small text-muted">A conexão profissional está ativa.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Connections;